import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowDownUp,
  Check,
  ChevronRight,
  EyeOff,
  Folder,
  Loader2,
  MessageSquare,
  Pencil,
  Plus,
  Trash2,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { getSessionKey, pickPrimaryMachineId, store, useStore } from "@/lib/store";
import type { SessionSource, SidebarSort } from "@/lib/store";
import type { Project, Session } from "@/lib/types";
import { hubApi, MACHINE_KIND_LABEL, MACHINE_KIND_ORDER } from "@/lib/hubApi";

function slugDisplayName(slug: string): string {
  const parts = slug.split("-").filter(Boolean);
  if (parts.length <= 2) return parts.join("/") || slug;
  return parts.slice(-2).join("/");
}

function relTime(ts?: number): string {
  if (!ts) return "";
  const diff = Date.now() - ts;
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

// relTime is derived at render time, so nothing re-renders when only the clock
// moves. Tick slowly to keep the labels honest without churning the tree.
function useRelTimeTick(intervalMs = 30_000): void {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
}

// Two project entries can describe the same directory while carrying different
// slugs (a trailing separator, a rescan after a cwd change), which used to
// render the same repo as two sidebar groups. Normalize the path so those
// collapse into one. Case is preserved: on case-sensitive filesystems /work/Foo
// and /work/foo are genuinely distinct projects.
function projectKey(project: Project): string {
  return (project.path || project.slug).replace(/[\\/]+$/, "");
}

interface SidebarGroup {
  source: SessionSource;
  project: Project;
  sessions: Session[];
  // A merged duplicate contributes sessions that live under its own slug, not
  // the surviving group's — keep the mapping so open still targets the right
  // project directory.
  slugBySessionId: Record<string, string>;
}

function buildGroups(
  source: SessionSource,
  projects: Project[],
  sessionsByProject: Record<string, Session[]>,
  sort: SidebarSort,
): SidebarGroup[] {
  const byKey = new Map<string, SidebarGroup>();
  for (const project of projects) {
    const key = projectKey(project);
    const group = byKey.get(key) ?? {
      source,
      project,
      sessions: [],
      slugBySessionId: {},
    };
    for (const session of sessionsByProject[project.slug] ?? []) {
      if (session.id in group.slugBySessionId) continue;
      group.slugBySessionId[session.id] = project.slug;
      group.sessions.push(session);
    }
    byKey.set(key, group);
  }
  return [...byKey.values()].map((group) => ({
    ...group,
    sessions: sortSessions(group.sessions, sort),
  }));
}

const SORT_OPTIONS: { value: SidebarSort; label: string }[] = [
  { value: "recent", label: "Recent" },
  { value: "status", label: "Status" },
  { value: "name", label: "Name" },
];

const STATUS_ORDER: Record<string, number> = { live: 0, mirror: 1, idle: 2 };

function sortSessions(sessions: Session[], sort: SidebarSort): Session[] {
  const sorted = [...sessions];
  switch (sort) {
    case "recent":
      sorted.sort(
        (a, b) =>
          (b.lastMessageAt ?? b.updatedAt) - (a.lastMessageAt ?? a.updatedAt),
      );
      break;
    case "status":
      sorted.sort(
        (a, b) =>
          (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9) ||
          (b.lastMessageAt ?? b.updatedAt) - (a.lastMessageAt ?? a.updatedAt),
      );
      break;
    case "name":
      sorted.sort((a, b) =>
        (a.title ?? a.id).localeCompare(b.title ?? b.id),
      );
      break;
  }
  return sorted;
}

interface InlineRenameProps {
  sessionId: string;
  currentTitle: string;
  onDone: () => void;
}

function InlineRename({ sessionId, currentTitle, onDone }: InlineRenameProps) {
  const [value, setValue] = useState(currentTitle);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  function commit() {
    const trimmed = value.trim();
    if (trimmed && trimmed !== currentTitle) {
      store.renameSession(sessionId, trimmed);
    }
    onDone();
  }

  return (
    <input
      ref={inputRef}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit();
        if (e.key === "Escape") onDone();
      }}
      className="w-full text-sm bg-transparent border-b border-primary/60 text-foreground outline-none px-0 py-0"
      maxLength={120}
    />
  );
}

interface SessionRowProps {
  source: SessionSource;
  session: Session;
  projectSlug: string;
  active: boolean;
  flashing: boolean;
  readOnly?: boolean;
  onOpen: (source: SessionSource, slug: string, id: string) => void;
  // Omitted where disposal is unsupported (remote sessions) so no dead confirm
  // dialog is offered.
  onDispose?: (id: string) => void;
}

function SessionRow({
  source,
  session: s,
  projectSlug,
  active,
  flashing,
  readOnly,
  onOpen,
  onDispose,
}: SessionRowProps) {
  const [renaming, setRenaming] = useState(false);
  const displayTitle = s.title || s.id.slice(0, 8);

  return (
    <div
      role="button"
      tabIndex={0}
      aria-current={active}
      className={cn(
        "group flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer transition-[background-color,border-color,box-shadow] border-l-2 min-w-0 overflow-hidden",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        flashing &&
          "bg-primary/10 border-primary/60 shadow-[0_0_0_1px_hsl(var(--primary)/0.25),0_0_18px_hsl(var(--primary)/0.18)]",
        active
          ? "bg-secondary/60 border-primary"
          : "border-transparent hover:bg-secondary/40",
      )}
      onClick={() => onOpen(source, projectSlug, s.id)}
      onKeyDown={(e) => {
        // The rename input and the action buttons keep their own key handling;
        // only act on keys aimed at the row itself.
        if (e.target !== e.currentTarget) return;
        if (e.key !== "Enter" && e.key !== " ") return;
        e.preventDefault();
        onOpen(source, projectSlug, s.id);
      }}
    >
      <MessageSquare
        className={cn(
          "h-3.5 w-3.5 shrink-0",
          active ? "text-primary" : "text-muted-foreground",
        )}
      />
      <div className="flex-1 min-w-0">
        {renaming ? (
          <InlineRename
            sessionId={s.id}
            currentTitle={displayTitle}
            onDone={() => setRenaming(false)}
          />
        ) : (
          <div
            className="block w-full text-sm text-foreground overflow-hidden whitespace-nowrap text-ellipsis"
            title={displayTitle}
          >
            {displayTitle}
          </div>
        )}
        <div className="text-[10px] text-muted-foreground flex items-center gap-1.5 min-w-0">
          <span className="truncate">
            {relTime(s.lastMessageAt ?? s.updatedAt)}
          </span>
          {s.status === "live" && (
            <span className="inline-flex items-center gap-1 text-primary">
              <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_6px_hsl(var(--primary))]" />
              live
            </span>
          )}
          {s.status === "mirror" && (
            <span className="inline-flex items-center gap-1 text-accent">
              <span className="h-1.5 w-1.5 rounded-full bg-accent animate-pulse" />
              mirror
            </span>
          )}
        </div>
      </div>
      {!readOnly && <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setRenaming(true);
          }}
          className="text-muted-foreground hover:text-primary p-0.5"
          aria-label="Rename session"
        >
          <Pencil className="h-3 w-3" />
        </button>
        {onDispose && <Dialog>
          <DialogTrigger asChild>
            <button
              type="button"
              onClick={(e) => e.stopPropagation()}
              className="text-muted-foreground hover:text-destructive p-0.5"
              aria-label="Dispose session"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </DialogTrigger>
          <DialogContent className="glass glass-border">
            <DialogHeader>
              <DialogTitle>Dispose session?</DialogTitle>
              <DialogDescription>
                &ldquo;{displayTitle}&rdquo; will be closed. The JSONL file
                will be kept.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="ghost" size="sm">
                  Keep
                </Button>
              </DialogClose>
              <Button
                variant="destructive"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onDispose(s.id);
                }}
              >
                Dispose
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>}
      </div>}
    </div>
  );
}

interface ProjectGroupProps {
  source: SessionSource;
  project: Project;
  sessions: Session[];
  slugBySessionId: Record<string, string>;
  flashingSessionKeys: Set<string>;
  activeKey: string | null;
  expanded: boolean;
  readOnly?: boolean;
  onToggle: () => void;
  onOpen: (source: SessionSource, slug: string, id: string) => void;
  onDispose?: (id: string) => void;
}

function ProjectGroup({
  source,
  project,
  sessions,
  slugBySessionId,
  flashingSessionKeys,
  activeKey,
  expanded,
  readOnly,
  onToggle,
  onOpen,
  onDispose,
}: ProjectGroupProps) {
  return (
    <div className="mb-2">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs uppercase tracking-wide text-muted-foreground hover:text-foreground hover:bg-secondary/40 transition-colors"
      >
        <ChevronRight
          className={cn(
            "h-3 w-3 shrink-0 transition-transform",
            expanded && "rotate-90",
          )}
        />
        <Folder className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate flex-1 text-left font-semibold min-w-0">
          {project.repoName ?? slugDisplayName(project.slug)}
        </span>
        {project.worktreeLabel && (
          <span className="text-[9px] px-1 py-0.5 rounded bg-secondary/60 text-muted-foreground shrink-0">
            {project.worktreeLabel}
          </span>
        )}
        <span className="text-[10px] opacity-60 shrink-0">
          {sessions.length}
        </span>
      </button>
      {expanded && (
        <div className="mt-1 space-y-0.5">
          {sessions.map((s) => (
            <SessionRow
              key={s.id}
              source={source}
              session={s}
              projectSlug={slugBySessionId[s.id] ?? project.slug}
              active={getSessionKey(source, s.id) === activeKey}
              flashing={flashingSessionKeys.has(getSessionKey(source, s.id) ?? "")}
              readOnly={readOnly}
              onOpen={onOpen}
              onDispose={onDispose}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface NewSessionDialogProps {
  projects: Project[];
  defaultSlug: string | null;
}

function NewSessionDialog({ projects, defaultSlug }: NewSessionDialogProps) {
  const state = useStore();
  const [open, setOpen] = useState(false);
  const [machineId, setMachineId] = useState<string>("local");
  const [slug, setSlug] = useState<string>(
    defaultSlug ?? projects[0]?.slug ?? "",
  );
  const [cwd, setCwd] = useState<string>("");
  const [model, setModel] = useState<string>("");
  const [permissionMode, setPermissionMode] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [waking, setWaking] = useState<Record<string, string | null>>({});

  const remoteMachines = useMemo(() => {
    return Object.values(state.remote)
      .filter((r) => r.machine.machineId !== state.localMachineId)
      .sort((a, b) => {
        if (a.machine.online !== b.machine.online) return a.machine.online ? -1 : 1;
        return a.machine.hostname.localeCompare(b.machine.hostname);
      });
  }, [state.remote, state.localMachineId]);

  // The picker groups by the machine's stored kind. With only self-hosted
  // boxes registered this collapses to a single unlabelled group, so the
  // control looks exactly as it did before the category existed.
  const machineGroups = useMemo(
    () =>
      MACHINE_KIND_ORDER.map((kind) => ({
        kind,
        label: MACHINE_KIND_LABEL[kind],
        entries: remoteMachines.filter((r) => r.machine.kind === kind),
      })).filter((group) => group.entries.length > 0),
    [remoteMachines],
  );

  // Hosted mode has no "this machine"; default to the first online machine with
  // projects, falling back to the first machine overall (which, since
  // remoteMachines is sorted online-first, is an offline box only when nothing
  // is online — so its Wake button can show).
  const defaultMachineId = state.hostedMode
    ? pickPrimaryMachineId(state) ?? remoteMachines[0]?.machine.machineId ?? ""
    : "local";

  const machineProjects =
    machineId === "local"
      ? projects
      : state.remote[machineId]?.projects ?? [];
  const selectedProject = machineProjects.find((p) => p.slug === slug);

  function resetForMachine(nextMachineId: string) {
    const nextProjects =
      nextMachineId === "local" ? projects : state.remote[nextMachineId]?.projects ?? [];
    const s =
      (nextMachineId === "local" ? defaultSlug : null) ?? nextProjects[0]?.slug ?? "";
    setSlug(s);
    const p = nextProjects.find((x) => x.slug === s);
    setCwd(p?.path ?? "");
  }

  function openDialog(next: boolean) {
    setOpen(next);
    if (next) {
      setMachineId(defaultMachineId);
      resetForMachine(defaultMachineId);
      setModel("");
      setPermissionMode("");
      setError(null);
    }
  }

  // In hosted mode the dialog is reachable before the machine list and then the
  // chosen machine's projects have loaded (loadRemoteProjects is fire-and-forget
  // on a later tick). Recover both: pick a machine once machines arrive, and pick
  // a project once that machine's projects arrive — hence the slug==="" guard and
  // the state.remote dependency. The guard is idempotent (setState bails when the
  // value is unchanged) so it never clobbers a selection the user has made.
  useEffect(() => {
    if (
      open &&
      state.hostedMode &&
      remoteMachines.length > 0 &&
      (machineId === "" || slug === "")
    ) {
      const target = machineId || defaultMachineId;
      setMachineId(target);
      resetForMachine(target);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, state.hostedMode, machineId, slug, remoteMachines.length, state.remote]);

  async function triggerWake(id: string) {
    if (!state.hubToken) return;
    setWaking((prev) => ({ ...prev, [id]: "waking" }));
    try {
      await hubApi.wakeMachine(state.hubToken, id);
    } catch {
      // Whether a machine is wakeable at all comes from the payload now, so a
      // failure here is just a failure.
      setWaking((prev) => ({ ...prev, [id]: "error" }));
      return;
    }
    // Machine will flip to online via useHubStream machine.status event; clear transient state after a delay.
    setTimeout(() => {
      setWaking((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }, 8000);
  }

  async function submit() {
    if (!slug) return;
    setBusy(true);
    setError(null);
    try {
      if (machineId === "local") {
        await store.createSession(
          slug,
          cwd.trim() || undefined,
          permissionMode || undefined,
          model.trim() || undefined,
        );
      } else {
        await store.createRemoteSession(
          machineId,
          slug,
          cwd.trim() || undefined,
          permissionMode || undefined,
          model.trim() || undefined,
        );
      }
      setOpen(false);
    } catch (err) {
      // Keep the dialog open so the entered cwd/model/permission mode survive;
      // the message is shown inline rather than only in the global ErrorBanner.
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={openDialog}>
      <DialogTrigger asChild>
        <Button
          variant="default"
          size="sm"
          className="w-full justify-start gap-2 bg-primary/10 text-primary border border-primary/30 hover:bg-primary/20"
        >
          <Plus className="h-4 w-4" />
          New Session
        </Button>
      </DialogTrigger>
      <DialogContent className="glass glass-border">
        <DialogHeader>
          <DialogTitle className="gradient-text">New Session</DialogTitle>
          <DialogDescription>
            Spawn a fresh Claude Code session on the chosen machine and project.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {remoteMachines.length > 0 && (
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-wide text-muted-foreground">
                Machine
              </label>
              <select
                value={machineId}
                onChange={(e) => {
                  const v = e.target.value;
                  setMachineId(v);
                  resetForMachine(v);
                }}
                className="w-full h-9 px-3 rounded-md bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {!state.hostedMode && <option value="local">This machine</option>}
                {machineGroups.map((group) => {
                  const options = group.entries.map(({ machine }) => (
                    <option
                      key={machine.machineId}
                      value={machine.machineId}
                      disabled={!machine.online}
                    >
                      {machine.hostname}
                      {machine.online ? "" : ` (offline, ${relTime(machine.lastSeenAt)})`}
                    </option>
                  ));
                  return machineGroups.length > 1 ? (
                    <optgroup key={group.kind} label={group.label}>
                      {options}
                    </optgroup>
                  ) : (
                    options
                  );
                })}
              </select>
              {machineId !== "local" && (() => {
                const selected = remoteMachines.find((r) => r.machine.machineId === machineId);
                if (!selected || selected.machine.online) return null;
                const wakeState = waking[machineId];
                if (!selected.machine.wakeable) {
                  return (
                    <p className="text-xs text-muted-foreground">
                      This machine cannot be woken remotely.
                    </p>
                  );
                }
                if (wakeState === "error") {
                  return (
                    <p className="text-xs text-destructive">
                      Wake request failed. Try again later.
                    </p>
                  );
                }
                return (
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={wakeState === "waking"}
                      onClick={() => void triggerWake(machineId)}
                      className="h-7 gap-1.5 text-xs text-primary border border-primary/30 hover:bg-primary/10"
                    >
                      <Zap className="h-3 w-3" />
                      {wakeState === "waking" ? "Waking…" : "Wake"}
                    </Button>
                    {wakeState === "waking" && (
                      <span className="text-xs text-muted-foreground">
                        Machine is starting up…
                      </span>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
          <div className="space-y-1.5">
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              Project
            </label>
            <select
              value={slug}
              onChange={(e) => {
                const v = e.target.value;
                setSlug(v);
                const p = machineProjects.find((x) => x.slug === v);
                if (p) setCwd(p.path);
              }}
              className="w-full h-9 px-3 rounded-md bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {machineProjects.map((p) => (
                <option key={p.slug} value={p.slug}>
                  {p.repoName ?? p.slug}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              Working directory
            </label>
            <Input
              value={cwd}
              onChange={(e) => setCwd(e.target.value)}
              placeholder={selectedProject?.path ?? "/path/to/project"}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              Model (optional)
            </label>
            <Input
              value={model}
              onChange={(e) => setModel(e.target.value)}
              placeholder="blank = this machine's default (claude-opus-5 unless configured)"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              Permission mode (optional)
            </label>
            <select
              value={permissionMode}
              onChange={(e) => setPermissionMode(e.target.value)}
              className="w-full h-9 px-3 rounded-md bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">Default</option>
              <option value="acceptEdits">Accept Edits</option>
              <option value="auto">Auto</option>
              <option value="bypassPermissions">Bypass Permissions</option>
              <option value="dontAsk">Don&apos;t Ask</option>
              <option value="plan">Plan</option>
            </select>
          </div>
          {error && (
            <p className="text-xs text-destructive" role="alert">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => setOpen(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy || !slug}>
            {busy ? "Creating" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SortControl() {
  const { sidebarSort, hideEmptyProjects } = useStore();
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors px-1.5 py-1 rounded"
        aria-label="Sort sessions"
      >
        <ArrowDownUp className="h-3 w-3" />
        <span className="hidden sm:inline">
          {SORT_OPTIONS.find((o) => o.value === sidebarSort)?.label}
        </span>
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
          />
          <div className="absolute right-0 top-full mt-1 z-50 min-w-[140px] py-1 rounded-md glass glass-border shadow-lg">
            {SORT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  store.setSidebarSort(opt.value);
                  setOpen(false);
                }}
                className={cn(
                  "w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-secondary/40 transition-colors",
                  sidebarSort === opt.value && "text-primary",
                )}
              >
                {sidebarSort === opt.value && (
                  <Check className="h-3 w-3 text-primary" />
                )}
                {sidebarSort !== opt.value && <span className="w-3" />}
                {opt.label}
              </button>
            ))}
            <div className="border-t border-border/40 my-1" />
            <button
              type="button"
              onClick={() => {
                store.setHideEmptyProjects(!hideEmptyProjects);
                setOpen(false);
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-secondary/40 transition-colors"
            >
              {hideEmptyProjects ? (
                <Check className="h-3 w-3 text-primary" />
              ) : (
                <span className="w-3" />
              )}
              <EyeOff className="h-3 w-3" />
              Hide empty
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function SessionList() {
  const state = useStore();
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [flashingSessionKeys, setFlashingSessionKeys] = useState<Set<string>>(
    () => new Set(),
  );
  const previousStatusesRef = useRef<Record<string, Session["status"]>>({});
  const flashTimeoutsRef = useRef<Map<string, number>>(new Map());
  const hydratedRef = useRef(false);
  const activeKey = getSessionKey(state.selected.source, state.selected.sessionId);

  useRelTimeTick();

  const grouped = useMemo(() => {
    return buildGroups(
      "local",
      state.projects,
      state.sessionsByProject,
      state.sidebarSort,
    ).filter((g) => !state.hideEmptyProjects || g.sessions.length > 0);
  }, [
    state.projects,
    state.sessionsByProject,
    state.sidebarSort,
    state.hideEmptyProjects,
  ]);

  const remoteGroups = useMemo(() => {
    const entries = Object.values(state.remote).slice().sort((a, b) => {
      if (a.machine.online !== b.machine.online) return a.machine.online ? -1 : 1;
      return a.machine.hostname.localeCompare(b.machine.hostname);
    });
    // In hosted mode the primary machine stands in for "this machine": show all
    // of its projects (even ones with no sessions) so the user always has an
    // actionable starting point. Other machines still honor hideEmptyProjects.
    const primaryMachineId = state.hostedMode ? pickPrimaryMachineId(state) : null;
    return entries.map((remote) => {
      const isPrimary = remote.machine.machineId === primaryMachineId;
      return {
        machine: remote.machine,
        groups: buildGroups(
          remote.machine.machineId,
          remote.projects,
          remote.sessionsByProject,
          state.sidebarSort,
        ).filter((group) => isPrimary || !state.hideEmptyProjects || group.sessions.length > 0),
      };
    });
  }, [state.remote, state.sidebarSort, state.hideEmptyProjects, state.hostedMode]);

  useEffect(() => {
    const nextStatuses: Record<string, Session["status"]> = {};
    const sessions = [
      ...grouped.flatMap(({ source, sessions: projectSessions }) =>
        projectSessions.map((session) => ({ source, session })),
      ),
      ...remoteGroups.flatMap(({ machine, groups }) =>
        groups.flatMap(({ sessions }) => sessions.map((session) => ({ source: machine.machineId, session }))),
      ),
    ];

    if (!hydratedRef.current) {
      hydratedRef.current = true;
      for (const { source, session } of sessions) {
        nextStatuses[getSessionKey(source, session.id) ?? session.id] = session.status;
      }
      previousStatusesRef.current = nextStatuses;
      return;
    }

    for (const { source, session } of sessions) {
      const sessionKey = getSessionKey(source, session.id) ?? session.id;
      nextStatuses[sessionKey] = session.status;
      if (session.status !== "live") continue;
      if (previousStatusesRef.current[sessionKey] === "live") continue;

      setFlashingSessionKeys((current) => {
        const next = new Set(current);
        next.add(sessionKey);
        return next;
      });

      const existingTimeout = flashTimeoutsRef.current.get(sessionKey);
      if (existingTimeout) window.clearTimeout(existingTimeout);
      const timeout = window.setTimeout(() => {
        setFlashingSessionKeys((current) => {
          if (!current.has(sessionKey)) return current;
          const next = new Set(current);
          next.delete(sessionKey);
          return next;
        });
        flashTimeoutsRef.current.delete(sessionKey);
      }, 1800);
      flashTimeoutsRef.current.set(sessionKey, timeout);
    }

    previousStatusesRef.current = nextStatuses;
  }, [grouped, remoteGroups]);

  useEffect(() => {
    return () => {
      for (const timeout of flashTimeoutsRef.current.values()) {
        window.clearTimeout(timeout);
      }
      flashTimeoutsRef.current.clear();
    };
  }, []);

  function toggle(slug: string) {
    setCollapsed((c) => ({ ...c, [slug]: !c[slug] }));
  }

  return (
    <div className="flex flex-col h-full">
      <div className="p-3 space-y-2">
        <NewSessionDialog
          projects={state.projects}
          defaultSlug={state.selected.slug}
        />
        <div className="flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground/60 font-semibold">
            Projects
          </span>
          <SortControl />
        </div>
      </div>
      <ScrollArea className="flex-1 px-2">
        <div className="pb-4">
          {grouped.length === 0 && remoteGroups.length === 0 && (
            state.hostedMode && !state.remoteMachinesLoaded ? (
              <div className="px-3 py-8 flex items-center justify-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading machines
              </div>
            ) : (
              <div className="px-3 py-8 text-center text-xs text-muted-foreground">
                No projects yet.
              </div>
            )
          )}
          {!state.hostedMode && grouped.length > 0 && (
            <div className="px-2 pb-2 text-[10px] uppercase tracking-widest text-muted-foreground/60 font-semibold">
              This machine
            </div>
          )}
          {grouped.map(({ source, project, sessions, slugBySessionId }) => (
            <ProjectGroup
              key={`local:${project.slug}`}
              source={source}
              project={project}
              sessions={sessions}
              slugBySessionId={slugBySessionId}
              flashingSessionKeys={flashingSessionKeys}
              activeKey={activeKey}
              expanded={!collapsed[project.slug]}
              onToggle={() => toggle(project.slug)}
              onOpen={(source: SessionSource, slug: string, id: string) => {
                void store.openSession(slug, id, source);
              }}
              onDispose={(id) => store.disposeSession(id)}
            />
          ))}
          {remoteGroups.map(({ machine, groups }) => (
            <div key={machine.machineId} className="mb-3">
              <div className="px-2 py-2 text-xs text-muted-foreground flex items-center justify-between gap-2">
                <span className="truncate">{machine.hostname}</span>
                <span className="inline-flex items-center gap-1 shrink-0">
                  <span className={cn("h-2 w-2 rounded-full", machine.online ? "bg-primary" : "bg-muted-foreground/40")} />
                  {machine.online ? "online" : relTime(machine.lastSeenAt)}
                </span>
              </div>
              {groups.map(({ source, project, sessions, slugBySessionId }) => {
                const collapseKey = `${machine.machineId}:${project.slug}`;
                return (
                  <ProjectGroup
                    key={collapseKey}
                    source={source}
                    project={project}
                    sessions={sessions}
                    slugBySessionId={slugBySessionId}
                    flashingSessionKeys={flashingSessionKeys}
                    activeKey={activeKey}
                    expanded={!collapsed[collapseKey]}
                    readOnly={!machine.online}
                    onToggle={() => toggle(collapseKey)}
                    onOpen={(sessionSource: SessionSource, slug: string, id: string) => {
                      void store.openSession(slug, id, sessionSource);
                    }}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}

export default SessionList;
